import { appService } from "../App/app.service";
import { store } from "../util/store";

/**
 * Current RCC view-as team from Redux (null = all teams / logged-in context).
 */
export function getRccViewAsTeamFromStore() {
  const v = store.getState()?.app?.rccViewAsTeam;
  return v === undefined || v === null || v === "" ? null : String(v);
}

/**
 * Team filter list for RCC data APIs: view-as uses scoped first-level teams, else first-level then scorecard default.
 */
export function getEffectiveRccTeamsToFilter(firstLevelTeamNames, defaultTeamFilter) {
  const appState = store.getState()?.app || {};
  const showReps = Boolean(appState.rccShowReps);
  const viewAs = getRccViewAsTeamFromStore();
  if (viewAs) {
    if (showReps) {
      return [viewAs];
    }
    if (Array.isArray(firstLevelTeamNames) && firstLevelTeamNames.length > 0) {
      return firstLevelTeamNames;
    }
    return [viewAs];
  }
  if (showReps) {
    if (Array.isArray(defaultTeamFilter) && defaultTeamFilter.length > 0) {
      return defaultTeamFilter;
    }
    return [];
  }
  if (Array.isArray(firstLevelTeamNames) && firstLevelTeamNames.length > 0) {
    return firstLevelTeamNames;
  }
  if (Array.isArray(defaultTeamFilter) && defaultTeamFilter.length > 0) {
    return defaultTeamFilter;
  }
  return [];
}

/**
 * Fetches the team hierarchy and returns first-level team names.
 * When rccViewAsTeam is set, uses the selected team's manager hierarchy (server-side).
 * @returns {Promise<Array>} Promise resolving to a list of first-level team names.
 */
export const fetchFirstLevelTeamNames = () => {
  const appState = store.getState()?.app || {};
  const scope = getRccViewAsTeamFromStore() || "";
  if (
    appState.rccFirstLevelTeamsLoaded &&
    String(appState.rccFirstLevelTeamsScopeKey || "") === scope
  ) {
    return Promise.resolve(
      Array.isArray(appState.rccFirstLevelTeams) ? appState.rccFirstLevelTeams : []
    );
  }

  const viewOpts =
    scope.length > 0 ? { viewAsTeam: scope } : {};

  return appService
    .getRccTeams(1, viewOpts)
    .then((resp) => {
      const teamNames = Array.isArray(resp?.data) ? resp.data : [];
      store.dispatch({
        type: "set_rcc_first_level_teams",
        teams: teamNames,
        scopeKey: scope,
      });
      return teamNames;
    })
    .catch((error) => {
      console.error("Error fetching team hierarchy:", error);
      store.dispatch({
        type: "set_rcc_first_level_teams",
        teams: [],
        scopeKey: scope,
      });
      return [];
    });
};

/**
 * Fetches the default team filter from performance scorecard filters.
 * Inputs: none. Outputs: array of team names (empty if not set or "All").
 *
 * Team data is stored at the TOP LEVEL of Redux state (not inside state.user)
 * so it is never persisted to localStorage and always re-fetches fresh on page refresh.
 *
 * @returns {Promise<Array>} Promise resolving to default team list.
 */
export const fetchDefaultTeamFilter = () => {
  const appState = store.getState()?.app || {};
  if (appState.rccDefaultTeamFilterLoaded) {
    return Promise.resolve(
      Array.isArray(appState.rccDefaultTeamFilter) ? appState.rccDefaultTeamFilter : []
    );
  }

  return appService
    .getGenericScoreCardFilters()
    .then((resp) => {
      const message = resp?.message || {};
      const defaultTeam =
        message?.parentChildrenTeamFilter?.defaultValueTeam ||
        message?.parentChildrenTeamFilter?.parentChildrenTeamFilter
          ?.defaultValueTeam ||
        [];

      const teamList = Array.isArray(defaultTeam) ? defaultTeam.filter(Boolean) : [];
      const firstTeam = String(teamList[0] ?? "").toLowerCase();
      if (!firstTeam || firstTeam === "all") {
        store.dispatch({ type: "set_rcc_default_team_filter", teams: [] });
        return [];
      }

      store.dispatch({ type: "set_rcc_default_team_filter", teams: teamList });
      return teamList;
    })
    .catch((error) => {
      console.error("Error fetching default team filter:", error);
      store.dispatch({ type: "set_rcc_default_team_filter", teams: [] });
      return [];
    });
};

/**
 * Loads RCC global team filter scope (eligible flag + descendant team names for dropdown).
 */
export const fetchRccTeamFilterScope = () => {
  return appService
    .getRccTeamFilterScope()
    .then((resp) => {
      const data = resp?.data || {};
      const eligible = Boolean(data.eligible);
      const teams = Array.isArray(data.teams) ? data.teams : [];
      const teamHierarchy = data.teamHierarchy || null;
      store.dispatch({
        type: "set_rcc_team_filter_scope",
        eligible,
        teams,
        teamHierarchy,
      });
      return { eligible, teams, teamHierarchy };
    })
    .catch((error) => {
      console.error("Error fetching RCC team filter scope:", error);
      store.dispatch({
        type: "set_rcc_team_filter_scope",
        eligible: false,
        teams: [],
        teamHierarchy: null,
      });
      return { eligible: false, teams: [], teamHierarchy: null };
    });
};

/**
 * Fetches the preferred data period and current period for the user's company.
 *
 * @returns {Promise<{ period: "monthly" | "quarterly", currentPeriod: string }>}
 */
export const fetchCompanyPreferredDataPeriod = () => {
  return appService
    .getRccCompanyPreferredDataPeriod()
    .then((resp) => {
      const period = resp?.data?.period === "monthly" ? "monthly" : "quarterly";
      const currentPeriod =
        typeof resp?.data?.currentPeriod === "string" ? resp.data.currentPeriod : "";
      return { period, currentPeriod };
    })
    .catch((error) => {
      console.error("Error fetching company preferred data period:", error);
      return { period: "quarterly", currentPeriod: "" };
    });
};
